import type { TFunction } from 'i18next';
import type {
  MeasureDataByEntity,
  MeasureDataResponse,
} from '../../../../../api/work-periods-client/work-periods-client.type';
import { TableColumn } from '../../../../../components/table/table';
import { downloadCSV } from '../../../../../helpers/csv-download';
import { Text } from '../../../../../ui-library/typography/typography';
import type { CapacityTableRow, WorkloadDistributionCapacityData } from './table.type';

/**
 * Type guard to check if data has workload distribution capacity structure
 *
 * @param {unknown} data - Unknown data to type check
 * @returns {boolean} True if data matches WorkloadDistributionCapacityData structure, false otherwise
 */
const isWorkloadDistributionCapacityData = (data: unknown): data is WorkloadDistributionCapacityData => {
  return (
    data !== null &&
    typeof data === 'object' &&
    'workload_distribution_capacity' in data &&
    typeof (data as WorkloadDistributionCapacityData).workload_distribution_capacity === 'object'
  );
};

/**
 * Transforms raw project data into structured capacity table rows
 *
 * @param {MeasureDataByEntity | undefined} projectData - Raw project data by entity
 * @param {MeasureDataResponse | undefined} portfolioData - Portfolio-level data for calculations
 * @param {string[]} selectedCapacityTeams - Array of selected team IDs
 * @param {Array<{ id: string; name: string }> | undefined} projectIdToName - Mapping of project IDs to names
 * @returns {CapacityTableRow[]} Array of structured capacity table rows
 */
const transformCapacityData = (
  projectData: MeasureDataByEntity | undefined,
  portfolioData: MeasureDataResponse | undefined,
  selectedCapacityTeams: string[],
  projectIdToName: Array<{ id: string; name: string }> | undefined,
): CapacityTableRow[] => {
  const formattedPortfolioData = isWorkloadDistributionCapacityData(portfolioData) ? portfolioData : null;

  if (!projectData) {
    return [];
  }

  return Object.entries(projectData)
    .filter(([projectId]) => selectedCapacityTeams.includes(projectId))
    .map(([projectId, projectMeasures]) => {
      const project = projectIdToName?.find((p) => p.id === projectId);
      if (!project) {
        return null;
      }

      const formattedProjectMeasures = isWorkloadDistributionCapacityData(projectMeasures) ? projectMeasures : null;

      const totalCapacityTasks = formattedPortfolioData?.workload_distribution_capacity.capacity_tasks;
      const projectCapacityTasks = formattedProjectMeasures?.workload_distribution_capacity.capacity_tasks;
      const distribution =
        totalCapacityTasks && projectCapacityTasks && totalCapacityTasks > 0
          ? (projectCapacityTasks / totalCapacityTasks) * 100
          : 0;

      return {
        projectId,
        name: project.name,
        tasks: formattedProjectMeasures?.workload_distribution_capacity.capacity_tasks || 0,
        points: formattedProjectMeasures?.workload_distribution_capacity.capacity_points || 0,
        distribution,
      };
    })
    .filter((row): row is CapacityTableRow => row !== null);
};

/**
 * Returns an array of column configurations for the capacity table
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {TableColumn<CapacityTableRow>[]} Array of column configurations including:
 * - name: Team name column
 * - tasks: Work units capacity column
 * - points: Estimation capacity column
 * - distribution: Workload distribution percentage column
 */
const getCapacityTableColumns = (t: TFunction): TableColumn<CapacityTableRow>[] => [
  {
    key: 'name',
    label: t('common.teams'),
    sortable: true,
    render: (value) => <Text size="small">{value as string}</Text>,
  },
  {
    key: 'tasks',
    label: t('process_analysis.workload_distribution.capacity_work_units', {
      work_units: t('common.work_units'),
    }),
    sortable: true,
    render: (value) => (
      <Text size="small">
        {value} {t('common.work_units')}
      </Text>
    ),
  },
  {
    key: 'points',
    label: t('process_analysis.workload_distribution.capacity_estimation', {
      estimation: t('common.estimation'),
    }),
    sortable: true,
    render: (value) => (
      <Text size="small">
        {value} {t('common.estimation')}
      </Text>
    ),
  },
  {
    key: 'distribution',
    label: t('process_analysis.workload_distribution.workload_distribution'),
    sortable: true,
    render: (value) => <Text size="small">{(value as number).toFixed(0)}%</Text>,
  },
];

/**
 * Custom sorting function for capacity table data
 *
 * @param {CapacityTableRow[]} data - Array of capacity table rows to sort
 * @param {keyof CapacityTableRow} sortBy - Column key to sort by
 * @param {'asc' | 'desc'} direction - Sort direction (asc or desc)
 * @returns {CapacityTableRow[]} Sorted array of capacity table rows
 */
const customCapacitySort = (
  data: CapacityTableRow[],
  sortBy: keyof CapacityTableRow,
  direction: 'asc' | 'desc',
): CapacityTableRow[] => {
  return [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      return direction === 'asc' ? comparison : -comparison;
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      const comparison = aValue - bValue;
      return direction === 'asc' ? comparison : -comparison;
    }

    return 0;
  });
};

/**
 * Exports capacity data as CSV
 *
 * @param {CapacityTableRow[]} data - Array of capacity table rows
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @return {void}
 */
const exportCapacityCSV = (data: CapacityTableRow[], t: TFunction): void => {
  try {
    const csvData: (string | number)[][] = [];

    const headerRow = [
      t('common.teams'),
      t('process_analysis.workload_distribution.capacity_work_units', {
        work_units: t('common.work_units'),
      }),
      t('process_analysis.workload_distribution.capacity_estimation', {
        estimation: t('common.estimation'),
      }),
      t('process_analysis.workload_distribution.workload_distribution'),
    ];

    csvData.push(headerRow);

    data.forEach((row) => {
      const dataRow = [
        row.name,
        `${row.tasks} ${t('common.work_units')}`,
        `${row.points} ${t('common.estimation')}`,
        `${(row.distribution ?? 0).toFixed(0)}%`,
      ];
      csvData.push(dataRow);
    });

    downloadCSV(csvData, 'capacity.csv');
  } catch (error) {
    console.error('Error generating capacity CSV data:', error);
  }
};

export { customCapacitySort, exportCapacityCSV, getCapacityTableColumns, transformCapacityData };
